import { renderRichTextToHtml } from '@/composables/contentful'

import type { ContentfulPromotion } from '#contentful/ContentfulPromotion'

/**
 * Resolves a promotion's Contentful content into cart-row presentation.
 *
 * **The gate, title and image apply to synthesized rows only** — rows with no cart bundle behind them,
 * where the promotion is the only possible source. Free shipping today; order-level discounts when
 * those get a row. A granted-bundle reward is a real line in `cartBundles`, so its title and image come
 * from the bundle, including the customer's own ad-hoc preview.
 *
 * **The description applies to both kinds** (decided 2026-08-05). `rewardCartItemDescription` is now
 * the authored source for what used to be the hard-coded "Reward Unlocked!" line, so a content manager
 * controls that text on every reward row. See `rewardCartItemDescriptionHtml` for the chain.
 *
 * Every resolver falls back, so nothing has to be authored for a row to render sensibly.
 */

/**
 * Whether this promotion should appear as its own cart line item.
 *
 * Tested `=== true` rather than `!== false` on purpose: promotion entries created before
 * `showSynthesizedRewardCartItem` existed carry no value at all, and an unset gate must mean "don't
 * show" rather than "show". The field is opt-in per promotion.
 */
export function isRewardCartItemEnabled(promotion: ContentfulPromotion | undefined): boolean {
  return promotion?.showSynthesizedRewardCartItem === true
}

/**
 * Row title. `fallback` is expected to be the reward tier's own `label`, which already resolves
 * `rewardTrackerLabel → retailName → nameInternal` — so passing it composes the full chain
 * `rewardCartItemRetailName → rewardTrackerLabel → retailName → nameInternal` without duplicating it.
 */
export function rewardCartItemTitle(
  promotion: ContentfulPromotion | undefined,
  fallback: string
): string {
  return promotion?.rewardCartItemRetailName?.trim() || fallback
}

/**
 * 104 × 125 thumbnail, falling back to the Free Gift callout's detail image — already photographic and
 * already authored on gift promotions, so those get a sensible thumbnail with no extra authoring.
 *
 * `rewardTrackerIcon` is deliberately absent from the chain: it is drawn as a 32px tracker node glyph,
 * and stretching that into the thumbnail box looks worse than the row's empty grey fill, which the
 * board treats as a legitimate state.
 */
export function rewardCartItemImageUrl(promotion: ContentfulPromotion | undefined): string | null {
  return (
    promotion?.rewardCartItemListingImage?.file?.url ??
    promotion?.freeGiftCalloutDetailImage?.file?.url ??
    null
  )
}

/**
 * Description as sanitized HTML, or `null` when nothing is authored.
 *
 * This is the first link in a chain the callers complete, and it differs by row kind:
 *
 * - **Granted-bundle reward:** authored → the bundle's own `cartDescription` → "Reward Unlocked!"
 * - **Synthesized reward:** authored → "Reward Unlocked!" *(no bundle exists to fall back to)*
 *
 * Falling through to the bundle's `cartDescription` means an unauthored gift row typically shows its
 * package contents ("Includes (2) 5x7 prints") rather than "Reward Unlocked!". That is a deliberate
 * departure from the design board: it is more informative, and the row still reads as a reward from the
 * green FREE pill and the `$0.00`, so nothing is lost by the text carrying different information.
 *
 * The emptiness check is on the *rendered* text, not on the document's presence: a rich-text field an
 * author clicked into and left empty still serialises to a document, which renders as `<p></p>` — truthy
 * HTML that displays as a blank line. Stripping tags is safe here because the field's Contentful
 * validations permit only text, bold/italic marks and list nodes, so there is no non-text content to
 * lose.
 */
export function rewardCartItemDescriptionHtml(
  promotion: ContentfulPromotion | undefined
): string | null {
  const document = promotion?.rewardCartItemDescription

  if (!document || !hasText(document)) {
    return null
  }

  return renderRichTextToHtml(document)
}

/**
 * Whether a rich-text document contains any actual text, walking its nodes rather than rendering first.
 *
 * Needed because a field an author clicked into and left still serialises to a document — one empty
 * paragraph — which renders as `<p></p>`: truthy HTML that displays as a blank line. Checking the nodes
 * also means the DOMPurify pass in `renderRichTextToHtml` is skipped entirely for the empty case.
 */
function hasText(node: unknown): boolean {
  if (typeof node !== 'object' || node === null) {
    return false
  }

  const { value, content } = node as { value?: unknown; content?: unknown }

  if (typeof value === 'string' && value.trim()) {
    return true
  }

  return Array.isArray(content) && content.some(hasText)
}
